import React, { useEffect, useState } from 'react';
import useGameStore from '../../store/gameStore';
import { useLanguageStore } from '../../i18n';
import { Globe, Languages } from 'lucide-react';

const STORAGE_KEY = 'hst-intl-switch-asked';

/**
 * Modal shown once when the player reaches Phase 4 (International / European phase).
 * Proposes — in both languages — to switch the game to English.
 * The user can decline and remain in French. Decision is remembered.
 *
 * Storage note (security review 28/05/2026 v2) :
 *   The localStorage value is a tiny UX flag ("accepted" | "declined")
 *   used solely to prevent re-showing this modal. No secret, no PII, no
 *   session credential. Migrating to a server cookie would force a
 *   network round-trip on every page mount in Phase 4+ for no benefit.
 *   Explicit decision to keep localStorage.
 */
export const InternationalSwitchModal = () => {
  const phase = useGameStore((s) => s.phase);
  const { language, setLanguage } = useLanguageStore();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (phase < 4) return;
    if (language === 'en') return; // already in English — no need to ask
    try {
      const asked = localStorage.getItem(STORAGE_KEY);
      if (!asked) setOpen(true);
    } catch (e) {
      // localStorage unavailable (private mode, quota) → fall back to
      // showing the modal once per page session.
      // eslint-disable-next-line no-console
      console.warn('[InternationalSwitchModal] localStorage unavailable:', e);
      setOpen(true);
    }
  }, [phase, language]);

  const remember = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[InternationalSwitchModal] could not persist choice:', e);
    }
  };

  const accept = () => {
    setLanguage('en');
    remember('accepted');
    setOpen(false);
  };

  const decline = () => {
    remember('declined');
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[97] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="intl-switch-overlay"
    >
      <div
        className="bg-ink-800 border border-ink-600 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-500"
        data-testid="intl-switch-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-violet-600 to-violet-900 px-6 py-6 text-center relative overflow-hidden">
          <Globe className="w-10 h-10 text-white mx-auto mb-2 opacity-90" />
          <h2 className="font-display text-white font-black text-xl">
            International Expansion
          </h2>
          <p className="text-white/80 text-xs mt-1 font-mono uppercase tracking-wider">
            Expansion Internationale
          </p>
        </div>

        {/* Body — bilingual */}
        <div className="px-6 py-5 space-y-4">
          {/* English version */}
          <div className="flex items-start gap-3 pb-4 border-b border-ink-600">
            <div className="w-8 h-8 rounded-md bg-accent/15 border border-accent/30 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-mono font-bold text-accent">EN</span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-foreground font-semibold">
                Welcome to the European phase!
              </p>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                Your company now operates across Europe. Would you like to switch
                the game interface to English?
              </p>
            </div>
          </div>

          {/* French version */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-md bg-money/15 border border-money/30 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-mono font-bold text-money">FR</span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-foreground font-semibold">
                Bienvenue dans la phase Européenne !
              </p>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                Votre société opère désormais à l'international. Souhaitez-vous
                basculer l'interface en anglais ?
              </p>
            </div>
          </div>
        </div>

        {/* Footer — buttons */}
        <div className="px-6 pb-6 space-y-2">
          <button
            onClick={accept}
            className="w-full bg-accent text-white py-3 rounded-xl text-sm font-bold hover:bg-accent-hover transition-colors active:scale-[0.98] flex items-center justify-center gap-2"
            data-testid="intl-switch-accept"
          >
            <Languages className="w-4 h-4" />
            Switch to English &nbsp;·&nbsp; Passer en anglais
          </button>
          <button
            onClick={decline}
            className="w-full bg-ink-700 text-slate-300 hover:text-foreground hover:bg-ink-600 py-2.5 rounded-xl text-sm font-semibold transition-colors active:scale-[0.98] border border-ink-600"
            data-testid="intl-switch-decline"
          >
            Stay in French &nbsp;·&nbsp; Rester en français
          </button>
          <p className="text-[10px] text-slate-500 text-center font-mono uppercase tracking-wider mt-2">
            You can change language anytime from the sidebar
          </p>
        </div>
      </div>
    </div>
  );
};

export default InternationalSwitchModal;
